import { inject, Injectable } from '@angular/core';
import {
  MenuItemNested,
  MenuItemStrapiResponse,
  MenuSlug,
  MenuStrapiAttributes,
  StrapiCityEntityApi,
} from '@core/const';
import { StrapiMediaNormalizer } from '@core/utils';
import { map, Observable } from 'rxjs';
import { StrapiBaseUrlService } from './base-url.service';
import { StrapiCollectionEntityApiService } from './collection-entity-api.service';

interface MenuItemWithChildren extends MenuItemStrapiResponse {
  children: MenuItemWithChildren[];
}

@Injectable({
  providedIn: 'root',
})
export class StrapiMenuNestedApiService extends StrapiCollectionEntityApiService<MenuStrapiAttributes> {
  private strapiMediaNormalizer: StrapiMediaNormalizer = inject(StrapiMediaNormalizer);
  private readonly strapiBaseUrl = inject(StrapiBaseUrlService).baseUrl;

  constructor() {
    super('v2-menus');
  }

  getBySlug(slug: MenuSlug, baseStoreCode: string): Observable<MenuItemNested[]> {
    return this.getList({
      populate: {
        menu_items: {
          populate: ['citiesIncluded', 'citiesExcluded', 'icon', 'parent'],
        },
      },
      filters: { slug: { $eq: slug } },
      pagination: { page: 1, pageSize: 1 },
    }).pipe(
      map((menus) => menus[0]),
      map((menu) => menu.menu_items || []),
      map((items) => this.filterByCity(items, baseStoreCode)),
      map((items) => this.buildTree(items)),
      map((tree) => this.sortByOrder(tree)),
      map((tree) => tree.map((item) => this.normalizeMenuItem(item))),
    );
  }

  private filterByCity(items: MenuItemStrapiResponse[], baseStoreCode: string): MenuItemStrapiResponse[] {
    return items.filter((item) => {
      if (item.citiesIncluded?.length && !this.findCity(item.citiesIncluded, baseStoreCode)) {
        return false;
      }
      if (item.citiesExcluded?.length && this.findCity(item.citiesExcluded, baseStoreCode)) {
        return false;
      }

      return true;
    });
  }

  private findCity(cities: StrapiCityEntityApi[], baseStoreCode: string): boolean {
    return cities.some((city) => city.code === baseStoreCode);
  }

  private buildTree(items: MenuItemStrapiResponse[]): MenuItemWithChildren[] {
    const itemMap = new Map<number, MenuItemWithChildren>();
    const roots: MenuItemWithChildren[] = [];

    for (const item of items) {
      itemMap.set(item.id, { ...item, children: [] });
    }

    for (const item of items) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const current = itemMap.get(item.id)!;
      const parentId = item.parent?.id;
      if (parentId) {
        const parent = itemMap.get(parentId);
        if (parent) {
          parent.children.push(current);
        } else {
          roots.push(current);
        }
      } else {
        roots.push(current);
      }
    }

    return roots;
  }

  private sortByOrder(items: MenuItemWithChildren[]): MenuItemWithChildren[] {
    return items
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map((item) => ({
        ...item,
        children: this.sortByOrder(item.children),
      }));
  }

  private normalizeMenuItem(item: MenuItemWithChildren): MenuItemNested {
    return {
      id: item.id,
      title: item.title,
      url: item.url || undefined,
      target: item.target || '_self',
      citiesIncluded: item.citiesIncluded?.map((city) => city.code) || [],
      citiesExcluded: item.citiesExcluded?.map((city) => city.code) || [],
      icon:
        item.icon && this.strapiBaseUrl ? this.strapiMediaNormalizer.convert(item.icon, this.strapiBaseUrl) : undefined,
      children: item.children?.map((child) => this.normalizeMenuItem(child)),
      order: item.order || 0,
    };
  }
}
